import { Outlet, Navigate, useLocation } from 'react-router-dom'
import { useProfileStore } from '@/stores/profileStore'
import { useToastStore } from '@/stores/toastStore'
import { useState } from 'react'
import { SideNavigation } from './SideNavigation'
import { TopBar } from './TopBar'
import { BottomNavigation } from './BottomNavigation'
import { MoreBottomSheet } from './MoreBottomSheet'
import { Toast } from '@drive-without-fear/ui'

export function AppLayout() {
  const { profile } = useProfileStore()
  const { message, type, isVisible, hide } = useToastStore()
  const location = useLocation()
  const [isSideNavOpen, setIsSideNavOpen] = useState(false)
  const [isMoreSheetOpen, setIsMoreSheetOpen] = useState(false)

  const userRole = profile?.role || 'user'

  // Paid users who haven't finished the post-payment onboarding get a true
  // takeover: no sidebar, no top bar, no bottom nav. Mirrors the mobile
  // PaidWelcome stack screen above MainTabs. The user must complete the
  // assessment before any chrome returns.
  const isPaidWelcomeTakeover =
    profile?.account_tier === 'paid' && !profile?.paid_onboarding_completed

  if (isPaidWelcomeTakeover) {
    // Without chrome, any non-dashboard route would render its own content
    // (e.g. /u/voice-memos → placeholder page) with no way back. Force the
    // user onto the dashboard, which hosts the welcome view.
    //   - /u/dashboard: render normally (will show PaidWelcomeView)
    //   - /u/payment-success: allow through; the page itself redirects to
    //     /u/dashboard?from=payment as soon as the webhook lands
    //   - anywhere else: redirect to dashboard
    const path = location.pathname
    const allowedDuringTakeover = path === '/u/dashboard' || path === '/u/payment-success'
    if (!allowedDuringTakeover) {
      return <Navigate to="/u/dashboard" replace />
    }
    return (
      <div className="min-h-screen bg-[#F6F3EC]">
        <main className="min-h-screen">
          <Outlet />
        </main>
        <Toast message={message} type={type} isVisible={isVisible} onClose={hide} />
      </div>
    )
  }

  return (
    <div className="flex min-h-screen bg-[#F6F3EC]">
      {/* Side Navigation - Hidden on mobile, visible on desktop */}
      <SideNavigation
        isOpen={isSideNavOpen}
        onClose={() => setIsSideNavOpen(false)}
        userRole={userRole}
      />

      {/* Main content area — offset by sidebar width on desktop */}
      <div className="flex-1 flex flex-col min-h-screen lg:ml-64">
        {/* Top Bar */}
        <TopBar onMenuClick={() => setIsSideNavOpen(true)} />

        {/* Main content */}
        <main className="flex-1 p-4 lg:p-6 pb-20 lg:pb-6">
          <div className="max-w-6xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Bottom Navigation - Visible on mobile only */}
      <BottomNavigation onMoreClick={() => setIsMoreSheetOpen(true)} />

      {/* More Bottom Sheet */}
      <MoreBottomSheet
        isOpen={isMoreSheetOpen}
        onClose={() => setIsMoreSheetOpen(false)}
        userRole={userRole}
      />

      {/* Global Toast Notifications */}
      <Toast message={message} type={type} isVisible={isVisible} onClose={hide} />
    </div>
  )
}
